import { SecurityLevel } from "../types/cia";
import {
  BusinessImpactDetails,
  CIAComponentType,
  CIADetails,
  ROIEstimate,
} from "../types/cia-services";
import { StatusType } from "../types/common/StatusTypes";
import {
  AvailabilityDetail,
  CIAImpactSummaryWidgetProps,
  ConfidentialityDetail,
  IntegrityDetail,
  SecurityLevelWidgetProps,
} from "../types/widgets";

/**
 * Type guard utilities for the CIA compliance manager
 *
 * These utilities ensure type safety when working with domain-specific types.
 * Type guards help TypeScript narrow types at runtime, preventing type errors
 * and enabling safe property access.
 * 
 * @example
 * ```typescript
 * import { isSecurityLevel, isObject } from './typeGuards';
 * 
 * // Type guard for security level
 * const level: unknown = 'High';
 * if (isSecurityLevel(level)) {
 *   console.log(level.toUpperCase()); // TypeScript knows level is SecurityLevel
 * }
 * 
 * // Type guard for objects
 * const data: unknown = { key: 'value' };
 * if (isObject(data)) {
 *   console.log(data.key); // Safe access after type guard
 * }
 * ```
 */

/**
 * Type guard to check if an object is an AvailabilityDetail
 * 
 * @param obj - Value to check
 * @returns True if obj is an AvailabilityDetail
 * 
 * @example
 * ```typescript
 * const data: unknown = getAvailabilityData();
 * if (isAvailabilityDetail(data)) {
 *   console.log(data.uptime); // Safe to access uptime property
 *   console.log(data.recommendations); // Safe to access array
 * }
 * ```
 */
export function isAvailabilityDetail(obj: unknown): obj is AvailabilityDetail {
  if (!obj || typeof obj !== "object") return false;

  const detail = obj as Partial<AvailabilityDetail>;
  return (
    typeof detail.description === "string" &&
    typeof detail.businessImpact === "string" &&
    typeof detail.uptime === "string" &&
    Array.isArray(detail.recommendations)
  );
}

/**
 * Type guard to check if an object is an IntegrityDetail
 */
export function isIntegrityDetail(obj: unknown): obj is IntegrityDetail {
  if (!obj || typeof obj !== "object") return false;

  const detail = obj as Partial<IntegrityDetail>;
  return (
    typeof detail.description === "string" &&
    typeof detail.businessImpact === "string" &&
    Array.isArray(detail.recommendations)
  );
}

/**
 * Type guard to check if an object is a ConfidentialityDetail
 */
export function isConfidentialityDetail(
  obj: unknown
): obj is ConfidentialityDetail {
  if (!obj || typeof obj !== "object") return false;

  const detail = obj as Partial<ConfidentialityDetail>;
  return (
    typeof detail.description === "string" &&
    typeof detail.technical === "string" &&
    typeof detail.businessImpact === "string" &&
    typeof detail.protectionMethod === "string" &&
    Array.isArray(detail.recommendations) &&
    detail.recommendations.every((item) => typeof item === "string")
  );
}

/**
 * Type guard to check if a CIA detail object exists
 */
export function isValidCIADetail(
  detail: CIADetails | undefined | null
): detail is CIADetails {
  return detail !== undefined && detail !== null;
}

/**
 * Type guard to check if a value is a non-null object
 * 
 * Useful for safely checking if a value is an object before accessing properties.
 * Filters out null, arrays, and primitive values.
 * 
 * @param value - Value to check
 * @returns True if value is a non-null object (excludes arrays)
 * 
 * @example
 * ```typescript
 * isObject({})              // true
 * isObject({ key: 'val' })  // true
 * isObject(null)            // false
 * isObject([])              // false (arrays excluded)
 * isObject('string')        // false
 * isObject(123)             // false
 * 
 * // Usage in code
 * const data: unknown = getUserData();
 * if (isObject(data) && 'name' in data) {
 *   console.log(data.name); // Safe property access
 * }
 * ```
 */
export function isObject(
  value: unknown
): value is Record<string | number | symbol, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Type guard to check if a value is a string
 */
export function isString(value: unknown): value is string {
  return typeof value === "string";
}

/**
 * Type guard to check if a value is a number
 */
export function isNumber(value: unknown): value is number {
  return typeof value === "number" && !isNaN(value);
}

/**
 * Safely access a nested property in an object using a dot notation path
 * @param obj The object to access
 * @param path The path to the property, e.g. 'a.b.c' or 'a[0].b.c'
 * @param defaultValue The default value to return if the property doesn't exist
 * @returns The value at the path or the default value
 */
export function safeAccess<T = unknown>(
  obj: unknown,
  path: string | (string | number)[],
  defaultValue?: T
): T {
  if (obj == null) {
    return defaultValue as T;
  }

  const parts = Array.isArray(path)
    ? path
    : path.replace(/\[(\d+)\]/g, ".$1").split(".");

  let current: unknown = obj;

  for (const part of parts) {
    if (current == null || typeof current !== "object") {
      return defaultValue as T;
    }

    current = (current as Record<string | number, unknown>)[part];
  }

  return current !== undefined && current !== null
    ? (current as T)
    : (defaultValue as T);
}

/**
 * Ensures value is an array, or converts it to one if not
 */
export function ensureArray<T>(value: T | T[]): T[] {
  return Array.isArray(value) ? value : value ? [value] : [];
}

/**
 * Safely converts string to number, with a fallback value if conversion fails
 */
export function safeNumberConversion(
  value: string | number | undefined,
  fallback = 0
): number {
  if (typeof value === "number") return value;
  if (typeof value === "string") {
    const parsed = Number(value);
    return isNaN(parsed) ? fallback : parsed;
  }
  return fallback;
}

/**
 * Helper function to safely access CIA options with string keys
 * @param options The options object to access
 * @param key The string key that should be treated as SecurityLevel
 * @returns The option value or undefined if not found
 */
export function getSecurityLevelOption<T>(
  options: Record<SecurityLevel, T>,
  key: string | undefined
): T | undefined {
  const safeKey = key || "None";

  if (
    safeKey === "None" ||
    safeKey === "Low" ||
    safeKey === "Moderate" ||
    safeKey === "High" ||
    safeKey === "Very High"
  ) {
    return options[safeKey as SecurityLevel];
  }
  return undefined;
}

/**
 * Helper function to check if an object has a property
 * @param obj The object to check
 * @param prop The property to check for
 * @returns True if the object has the property
 */
export function hasProperty(obj: unknown, prop: string): boolean {
  return Boolean(obj && Object.prototype.hasOwnProperty.call(obj, prop));
}

/**
 * Type guard for ROI metrics objects
 * @param value - The value to check
 * @returns True if the value is a valid ROI metrics object
 */
export function isROIMetrics(value: unknown): boolean {
  return (
    isObject(value) &&
    typeof value.returnRate === "string" &&
    typeof value.description === "string"
  );
}

/**
 * Type guard for technical implementation details
 * @param value - The value to check
 * @returns True if the value is a valid technical implementation details object
 */
export function isTechnicalImplementationDetails(value: unknown): boolean {
  return (
    isObject(value) &&
    typeof value.description === "string" &&
    Array.isArray(value.implementationSteps) &&
    isObject(value.effort) &&
    typeof value.effort.development === "string" &&
    typeof value.effort.maintenance === "string" &&
    typeof value.effort.expertise === "string"
  );
}

/**
 * Type guard for security resource objects
 * @param value - The value to check
 * @returns True if the value is a valid security resource object
 */
export function isSecurityResource(value: unknown): boolean {
  return (
    isObject(value) &&
    typeof value.title === "string" &&
    typeof value.description === "string" &&
    typeof value.url === "string" &&
    typeof value.category === "string" &&
    Array.isArray(value.tags) &&
    typeof value.relevanceScore === "number" &&
    typeof value.type === "string"
  );
}

/**
 * Type guard for basic widget props
 * @param value - The value to check
 * @returns True if the value has the required widget properties
 */
export function hasWidgetProps(value: unknown): boolean {
  return (
    isObject(value) &&
    typeof value.title === "string" &&
    typeof value.description === "string" &&
    typeof value.icon === "string"
  );
}

/**
 * Type guard for basic widget props
 * @param value - The value to check
 * @returns True if the value has the required widget properties
 */
export function isWidgetProps(value: unknown): boolean {
  if (!isObject(value)) return false;

  if (hasProperty(value, "title") && !isString(value.title)) return false;
  if (hasProperty(value, "description") && !isString(value.description))
    return false;
  if (hasProperty(value, "icon") && !isString(value.icon)) return false;

  return (
    hasProperty(value, "title") &&
    hasProperty(value, "description") &&
    hasProperty(value, "icon")
  );
}

/**
 * Checks if an object is a valid security profile
 */
export function isSecurityProfile(obj: unknown): boolean {
  if (!isObject(obj)) return false;

  return (
    hasProperty(obj, "availability") &&
    isString(obj.availability) &&
    hasProperty(obj, "integrity") &&
    isString(obj.integrity) &&
    hasProperty(obj, "confidentiality") &&
    isString(obj.confidentiality) &&
    hasProperty(obj, "overall") &&
    isString(obj.overall)
  );
}

/**
 * Checks if an object is a valid compliance status
 *
 * @param obj - Object to check
 * @returns True if the object is a valid compliance status
 */
export function isComplianceStatus(obj: unknown): boolean {
  if (!obj || typeof obj !== "object") return false;

  const typedObj = obj as Record<string, unknown>;

  if (!Array.isArray(typedObj.compliantFrameworks)) return false;
  if (!Array.isArray(typedObj.partiallyCompliantFrameworks)) return false;
  if (!Array.isArray(typedObj.nonCompliantFrameworks)) return false;

  if (
    typedObj.remediationSteps !== undefined &&
    !Array.isArray(typedObj.remediationSteps)
  )
    return false;
  if (typedObj.requirements !== undefined && !Array.isArray(typedObj.requirements))
    return false;

  if (typedObj.status !== undefined && typeof typedObj.status !== "string") return false;
  if (
    typedObj.complianceScore !== undefined &&
    typeof typedObj.complianceScore !== "number"
  )
    return false;
  if (typedObj.score !== undefined && typeof typedObj.score !== "number") return false;

  return true;
}

/**
 * Checks if an object is a valid compliance framework
 *
 * @param obj - Object to check
 * @returns True if the object is a valid compliance framework
 */
export function isComplianceFramework(obj: unknown): boolean {
  if (!obj) {
    return false;
  }

  if (typeof obj === "string") {
    return true;
  }

  if (typeof obj !== "object") {
    return false;
  }

  const typedObj = obj as Record<string, unknown>;

  if (!hasProperty(obj, "name") || typeof typedObj.name !== "string") {
    return false;
  }

  const hasRequiredProperties =
    hasProperty(obj, "status") ||
    hasProperty(obj, "description") ||
    hasProperty(obj, "requiredAvailabilityLevel") ||
    hasProperty(obj, "requiredIntegrityLevel") ||
    hasProperty(obj, "requiredConfidentialityLevel");

  if (!hasRequiredProperties) {
    return false;
  }


  return true;
}

/**
 * Checks if an object is a valid ROI metric details object
 */
export function isROIMetricDetails(obj: unknown): boolean {
  if (!isObject(obj)) return false;
  return (
    hasProperty(obj, "amount") &&
    isString(obj.amount) &&
    hasProperty(obj, "percentage") &&
    isString(obj.percentage) &&
    hasProperty(obj, "timeframe") &&
    isString(obj.timeframe)
  );
}

/**
 * Type guard utilities for the CIA compliance manager
 *
 * These utilities ensure type safety when working with domain-specific types.
 */

/**
 * Type guard for SecurityLevel
 *
 * @param value - Value to check
 * @returns True if the value is a valid SecurityLevel
 */
export function isSecurityLevel(value: unknown): value is SecurityLevel {
  return (
    typeof value === "string" &&
    ["None", "Low", "Moderate", "High", "Very High"].includes(value)
  );
}

/**
 * Type guard for CIAComponentType
 *
 * @param value - Value to check
 * @returns True if the value is a valid CIAComponentType
 */
export function isCIAComponentType(value: unknown): value is CIAComponentType {
  return (
    typeof value === "string" &&
    ["confidentiality", "integrity", "availability"].includes(value)
  );
}

/**
 * Type guard for CIADetails
 *
 * @param value - Value to check
 * @returns True if the value has the required properties of CIADetails
 */
export function isCIADetails(value: unknown): value is CIADetails {
  if (!value || typeof value !== "object") return false;

  const obj = value as Record<string, unknown>;

  return (
    typeof obj.description === "string" &&
    typeof obj.technical === "string" &&
    typeof obj.businessImpact === "string" &&
    typeof obj.capex === "number" &&
    typeof obj.opex === "number" &&
    typeof obj.bg === "string" &&
    typeof obj.text === "string" &&
    Array.isArray(obj.recommendations)
  );
}

/**
 * Type guard for checking if a value is a valid widget props object
 * @param value - The value to check
 * @returns True if the value is a valid widget props object
 */

/**
 * Type guard for checking if a value is a SecurityLevelWidgetProps
 * @param value - The value to check
 * @returns True if the value is a valid SecurityLevelWidgetProps
 */
export function isSecurityLevelWidgetProps(
  value: unknown
): value is SecurityLevelWidgetProps {
  if (!isWidgetProps(value)) return false;

  const val = value as Record<string, unknown>;

  return (
    hasProperty(val, "availabilityLevel") &&
    isSecurityLevel(val.availabilityLevel) &&
    hasProperty(val, "integrityLevel") &&
    isSecurityLevel(val.integrityLevel) &&
    hasProperty(val, "confidentialityLevel") &&
    isSecurityLevel(val.confidentialityLevel)
  );
}

/**
 * Type guard for CIAImpactSummaryWidgetProps
 * @param value - The value to check
 * @returns True if the value is a valid CIAImpactSummaryWidgetProps
 */
export function isCIAImpactSummaryWidgetProps(
  value: unknown
): value is CIAImpactSummaryWidgetProps {
  if (!isWidgetProps(value)) return false;

  const val = value as Record<string, unknown>;

  return (
    hasProperty(val, "availabilityLevel") &&
    isSecurityLevel(val.availabilityLevel) &&
    hasProperty(val, "integrityLevel") &&
    isSecurityLevel(val.integrityLevel) &&
    hasProperty(val, "confidentialityLevel") &&
    isSecurityLevel(val.confidentialityLevel)
  );
}

/**
 * Type guard for BusinessImpactDetails
 * @param value - The value to check
 * @returns True if the value is a valid BusinessImpactDetails
 */
export function isBusinessImpactDetails(
  value: unknown
): value is BusinessImpactDetails {
  if (!isObject(value)) return false;

  if (typeof value.summary !== "string") return false;

  const hasAnyImpact = [
    "financial",
    "operational",
    "reputational",
    "strategic",
    "regulatory",
  ].some((prop) => hasProperty(value, prop) && isObject(value[prop]));

  return hasAnyImpact;
}

/**
 * Type guard for ROI estimate
 * @param value - The value to check
 * @returns True if the value is a valid ROI estimate
 */
export function isROIEstimate(value: unknown): value is ROIEstimate {
  return (
    isObject(value) &&
    typeof value.returnRate === "string" &&
    typeof value.description === "string"
  );
}

/**
 * Checks if an object is a valid widget config
 */
export function isWidgetConfig(obj: unknown): boolean {
  if (!isObject(obj)) return false;
  return hasProperty(obj, "type") && isString(obj.type);
}

/**
 * Checks if an object has a specific tag value
 */
export function hasTagValue(obj: unknown, tagValue: string): boolean {
  if (!isObject(obj) || !hasProperty(obj, "tags") || !Array.isArray(obj.tags)) {
    return false;
  }
  return obj.tags.includes(tagValue);
}

/**
 * Parse a risk level from a string or number
 *
 * @param riskLevel - Risk level to parse
 * @returns Numeric risk level
 */
export function parseRiskLevel(
  riskLevel: string | number | null | undefined
): number {
  if (riskLevel == null) return 0;

  if (riskLevel === "Critical") return 0;

  if (typeof riskLevel === "number") return riskLevel;

  const parsed = Number(riskLevel);
  if (!isNaN(parsed)) return parsed;

  const riskLevelMap: Record<string, number> = {
    critical: 4,
    high: 3,
    medium: 2,
    low: 1,
    minimal: 0,
  };

  const normalizedLevel = riskLevel.toLowerCase();
  if (normalizedLevel in riskLevelMap) {
    return riskLevelMap[normalizedLevel];
  }

  return 0;
}

/**
 * Extracts CIA security levels from an object
 */
export function extractSecurityLevels(obj: unknown): {
  availability: string;
  integrity: string;
  confidentiality: string;
} {
  if (!isObject(obj)) {
    return {
      availability: "None",
      integrity: "None",
      confidentiality: "None",
    };
  }

  return {
    availability: String(obj.availability || "None"),
    integrity: String(obj.integrity || "None"),
    confidentiality: String(obj.confidentiality || "None"),
  };
}

/**
 * Calculates the implementation cost from a cost object
 */
export function getImplementationCost(costObj: unknown): number {
  if (!isObject(costObj)) return 0;

  let total = 0;
  if (hasProperty(costObj, "capex") && isNumber(costObj.capex)) {
    total += costObj.capex;
  }
  if (hasProperty(costObj, "opex") && isNumber(costObj.opex)) {
    total += costObj.opex;
  }
  if (hasProperty(costObj, "fte") && isNumber(costObj.fte)) {
    total += costObj.fte * 100000; // Assuming $100k per FTE
  }
  return total;
}

/**
 * Domain-specific type guards for consistent type checking
 *
 * ## Business Perspective
 *
 * These type guards ensure reliable runtime validation of critical
 * security and compliance data types, reducing bugs and improving
 * the stability of security assessments and compliance mappings. 🛡️
 *
 * Consistent type validation is essential for maintaining data integrity
 * across the application's security models and calculations.
 */

/**
 * Type guard for business impact category
 *
 * @param value - Value to check
 * @returns Whether the value is a valid business impact category
 */
export function isBusinessImpactCategory(value: unknown): boolean {
  if (typeof value !== "string") return false;

  return [
    "financial",
    "operational",
    "regulatory",
    "reputational",
    "strategic",
  ].includes(value.toLowerCase());
}

/**
 * Type guard for compliance framework name
 *
 * @param value - Value to check
 * @returns Whether the value is a valid compliance framework name
 */
export function isComplianceFrameworkName(value: unknown): boolean {
  if (typeof value !== "string") return false;

  return [
    "ISO 27001",
    "NIST CSF",
    "NIST 800-53",
    "GDPR",
    "HIPAA",
    "PCI DSS",
    "SOC2",
  ].includes(value);
}

/**
 * Type guard for compliance framework object
 *
 * @param value - Value to check
 * @returns Whether the value is a valid compliance framework object
 */
export function isComplianceFrameworkObject(value: unknown): boolean {
  if (!value || typeof value !== "object") return false;

  const obj = value as Record<string, unknown>;

  return (
    typeof obj.name === "string" &&
    typeof obj.description === "string" &&
    (Array.isArray(obj.requirements) || obj.requirements === undefined)
  );
}

/**
 * Type guard for risk level
 *
 * @param value - Value to check
 * @returns Whether the value is a valid risk level
 */
export function isRiskLevel(value: unknown): boolean {
  if (typeof value !== "string") return false;

  return [
    "Critical",
    "Critical Risk",
    "High",
    "High Risk",
    "Medium",
    "Medium Risk",
    "Low",
    "Low Risk",
    "Minimal",
    "Minimal Risk",
    "Unknown",
    "Unknown Risk",
  ].includes(value);
}

/**
 * Type guard for widget
 *
 * @param value - Value to check
 * @returns Whether the value is a valid widget
 */
export function isWidget(value: unknown): boolean {
  if (!value || typeof value !== "object") return false;

  const obj = value as Record<string, unknown>;

  return (
    typeof obj.id === "string" &&
    typeof obj.type === "string" &&
    typeof obj.title === "string"
  );
}

/**
 * Type guard for widget type
 *
 * @param value - Value to check
 * @returns Whether the value is a valid widget type
 */
export function isWidgetType(value: unknown): boolean {
  if (typeof value !== "string") return false;

  return [
    "security-level",
    "security-summary",
    "security-visualization",
    "compliance-status",
    "value-creation",
    "cost-estimation",
    "business-impact",
    "technical-details",
    "availability-impact",
    "integrity-impact",
    "confidentiality-impact",
    "security-resources",
  ].includes(value);
}

/**
 * Type guard to check if a value is a boolean
 * @param value - Value to check
 * @returns True if the value is a boolean
 */
export function isBoolean(value: unknown): value is boolean {
  return typeof value === "boolean";
}

/**
 * Type guard to check if a value is an array
 * @param value - Value to check
 * @returns True if the value is an array
 */
export function isArray(value: unknown): value is unknown[] {
  return Array.isArray(value);
}

/**
 * Type guard to check if a value is a function.
 * Note: `typeof` returns "function" for both callables and class constructors,
 * so the `Function` type is the correct predicate here.
 * @param value - Value to check
 * @returns True if the value is a function
 */
// eslint-disable-next-line @typescript-eslint/no-unsafe-function-type -- typeof check matches all function-like values including constructors
export function isFunction(value: unknown): value is Function {
  return typeof value === "function";
}

/**
 * Type guard to check if a value is null
 * @param value - Value to check
 * @returns True if the value is null
 */
export function isNull(value: unknown): value is null {
  return value === null;
}

/**
 * Type guard to check if a value is undefined
 * @param value - Value to check
 * @returns True if the value is undefined
 */
export function isUndefined(value: unknown): value is undefined {
  return value === undefined;
}

/**
 * Type guard to check if a value is nullish (null or undefined)
 * @param value - Value to check
 * @returns True if the value is null or undefined
 */
export function isNullish(value: unknown): value is null | undefined {
  return value === null || value === undefined;
}

/**
 * Type guard to check if a value is a Date object
 * @param value - Value to check
 * @returns True if the value is a Date object
 */
export function isDate(value: unknown): value is Date {
  return value instanceof Date;
}

/**
 * Type guard to check if a value is an Error object
 * @param value - Value to check
 * @returns True if the value is an Error object
 */
export function isError(value: unknown): value is Error {
  return value instanceof Error;
}

/**
 * Check if a value can be used as an object key
 * @param value - Value to check
 * @returns True if the value can be used as an object key
 */
export function isValidKey(value: unknown): value is string | number | symbol {
  return isString(value) || isNumber(value) || typeof value === "symbol";
}

/**
 * Type guard to check if a value is a valid CIA component
 * Alias for isCIAComponentType for backward compatibility
 * @param value - Value to check
 * @returns True if the value is a valid CIA component
 */
export function isCIAComponent(value: unknown): value is CIAComponentType {
  return isCIAComponentType(value);
}

/**
 * Safely converts a string to a SecurityLevel, with fallback
 * @param value The value to convert
 * @param fallback The fallback value (defaults to "Moderate")
 * @returns A valid SecurityLevel
 */
export const toSecurityLevel = (
  value: unknown,
  fallback: SecurityLevel = "Moderate"
): SecurityLevel => {
  if (isSecurityLevel(value)) return value;
  return fallback;
};

/**
 * Type guard to check if a value is a valid StatusType
 * @param value The value to check
 * @returns Whether the value is a valid StatusType
 */
export function isStatusType(value: unknown): value is StatusType {
  if (typeof value !== "string") return false;

  const validStatusTypes: StatusType[] = [
    "success",
    "info",
    "warning",
    "error",
    "neutral",
    "purple",
  ];

  return validStatusTypes.includes(value as StatusType);
}

/**
 * Safely converts a string to a StatusType, with fallback
 * @param value The value to convert
 * @param fallback The fallback value (defaults to "neutral")
 * @returns A valid StatusType
 */
export const toStatusType = (
  value: unknown,
  fallback: StatusType = "neutral"
): StatusType => {
  if (isStatusType(value)) return value;
  return fallback;
};

/**
 * Type guard to check if an object has a specific method
 * @param obj The object to check
 * @param methodName The method name to check for
 * @returns True if the object has the method as a function
 */
export function hasMethod<T extends object, K extends PropertyKey>(
  obj: T | null | undefined,
  methodName: K
): obj is T & Record<K, (...args: unknown[]) => unknown> {
  return (
    obj !== null &&
    obj !== undefined &&
    typeof obj === "object" &&
    methodName in obj &&
    typeof (obj as Record<PropertyKey, unknown>)[methodName] === "function"
  );
}
